import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GlobalAutoCompleteMultiSelectNoFormik from '../../../components/GlobalAutoCompleteMultiSelectNoFormik';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { useDispatch } from 'react-redux';
import {
  getAddedByList,
  getCourseByList,
  getLeadExpenseCSDTable,
  getLeadExpenseDashboardCards,
  getLeadStageByList,
  getLeadStatusByList,
  getLeadTypesByList,
  getOwnerList,
  getPreferredProjectByList,
  getSourceByList,
  getSubSourceByList,
} from '../../../redux/features/CRDslice';
import { useSelector } from 'react-redux';

export default function DashboardFilters({ fromDate, toDate, CSD }) {
  const [filters, setFilters] = useState({
    pref_course: null,
    lead_stage: null,
    lead_status: null,
    lead_type: null,
    sub_source: null,
    source: null,
    added_by: null,
    owner: null,
    pref_project: null,
  });

  const abortCardRef = useRef(null);
  const abortTabledRef = useRef(null);

  const dispatch = useDispatch();

  // filter Lists
  const addedByList = useSelector((state) => state.crd.addedByList.data);
  const addedByListLoading = useSelector((state) => state.crd.addedByList.loading);

  const leadStageByList = useSelector((state) => state.crd.leadStageByList.data);
  const leadStageByListLoading = useSelector((state) => state.crd.leadStageByList.loading);

  const courseByList = useSelector((state) => state.crd.courseByList.data);
  const courseByListLoading = useSelector((state) => state.crd.courseByList.loading);

  const sourceByList = useSelector((state) => state.crd.sourceByList.data);
  const sourceByListLoading = useSelector((state) => state.crd.sourceByList.loading);

  const subSourceByList = useSelector((state) => state.crd.subSourceByList.data);
  const subSourceByListLoading = useSelector((state) => state.crd.subSourceByList.loading);

  const leadTypesByList = useSelector((state) => state.crd.leadTypesByList.data);
  const leadTypesByListLoading = useSelector((state) => state.crd.leadTypesByList.loading);

  const leadStatusByList = useSelector((state) => state.crd.leadStatusByList.data);
  const leadStatusByListLoading = useSelector((state) => state.crd.leadStatusByList.loading);

  const preferredProjectByList = useSelector((state) => state.crd.preferredProjectByList.data);
  const preferredProjectByListLoading = useSelector((state) => state.crd.preferredProjectByList.loading);

  const ownerList = useSelector((state) => state.crd.ownerList.data);
  const ownerListLoading = useSelector((state) => state.crd.ownerList.loading);

  const mainTableState = useSelector((state) => state.crd.leadExpenseCSDTable.tableState);

  const fetchCardsData = useCallback(
    ({ fromDate, toDate }) => {
      const cardsAPI = dispatch(
        getLeadExpenseDashboardCards({ data: { ...filters, fromDate, toDate }, type: CSD ? 'csd' : 'ogd' }),
      );
      abortCardRef.current = cardsAPI;
    },
    [dispatch, filters],
  );

  const fetchTableData = useCallback(
    ({ fromDate, toDate, tableState = { page: 1, limit: 50 } }) => {
      const cardsAPI = dispatch(
        getLeadExpenseCSDTable({ data: { ...filters, fromDate, toDate, ...tableState }, type: CSD ? 'csd' : 'ogd' }),
      );
      abortTabledRef.current = cardsAPI;
    },
    [dispatch, filters],
  );

  const handleSubmit = () => {
    fetchCardsData({ fromDate, toDate });
    fetchTableData({ fromDate, toDate, tableState: mainTableState });
  };

  // Main Card useEffect
  useEffect(() => {
    fetchCardsData({ fromDate, toDate });
    return () => {
      abortCardRef?.current?.abort?.();
    };
  }, [fromDate, toDate]);

  // Main Table useEffect
  useEffect(() => {
    fetchTableData({ fromDate, toDate, tableState: mainTableState });
    return () => {
      abortTabledRef?.current?.abort?.();
    };
  }, [fromDate, toDate, mainTableState]);

  const setFilterHelper = useCallback((obj) => {
    setFilters((prev) => ({ ...prev, ...obj }));
  }, []);

  // Mount on useEffect
  useEffect(() => {
    const addedBySignal = dispatch(getAddedByList());
    const leadStageSignal = dispatch(getLeadStageByList());
    const courseSignal = dispatch(getCourseByList());
    const sourceSignal = dispatch(getSourceByList());
    const subSourceSignal = dispatch(getSubSourceByList());
    const leadTypesSignal = dispatch(getLeadTypesByList());
    const leadStatusSignal = dispatch(getLeadStatusByList());
    const preferredProjectSignal = dispatch(getPreferredProjectByList());
    const ownerListSignal = !CSD ? dispatch(getOwnerList()) : null;

    return () => {
      addedBySignal.abort();
      leadStageSignal.abort();
      courseSignal.abort();
      sourceSignal.abort();
      subSourceSignal.abort();
      leadTypesSignal.abort();
      leadStatusSignal.abort();
      preferredProjectSignal.abort();
      if (ownerListSignal) {
        ownerListSignal.abort();
      }
    };
  }, [dispatch, CSD]);

  const AutoCompleteJson = useMemo(
    () => [
      ...(CSD
        ? []
        : [
            {
              value: 'name',
              label: 'Owner',
              id: 'id',
              visible: true,
              data: ownerList,
              loading: ownerListLoading,
              setKeyName: 'id',
              onChange: (val) => setFilterHelper({ owner: val }),
            },
          ]),
      {
        value: 'name',
        label: 'Lead Added By',
        id: 'sl_no',
        visible: true,
        data: addedByList,
        loading: addedByListLoading,
        onChange: (val) => setFilterHelper({ added_by: val }),
      },
      {
        value: 'name',
        label: 'Source',
        id: 'sl_no',
        visible: true,
        data: sourceByList,
        loading: sourceByListLoading,
        onChange: (val) => setFilterHelper({ source: val }),
      },
      {
        value: 'name',
        id: 'sl_no',
        label: 'Sub Sources',
        visible: false,
        data: subSourceByList,
        loading: subSourceByListLoading,
        onChange: (val) => setFilterHelper({ sub_source: val }),
      },
      {
        value: 'name',
        id: 'sl_no',
        label: 'Lead Type',
        visible: false,
        data: leadTypesByList,
        loading: leadTypesByListLoading,
        onChange: (val) => setFilterHelper({ lead_type: val }),
      },
      {
        value: 'name',
        id: 'sl_no',
        label: 'Lead Status',
        visible: true,
        data: leadStatusByList,
        loading: leadStatusByListLoading,
        onChange: (val) => setFilterHelper({ lead_status: val }),
      },
      {
        value: 'name',
        id: 'sl_no',
        label: 'Lead Stage',
        visible: true,
        data: leadStageByList,
        loading: leadStageByListLoading,
        onChange: (val) => setFilterHelper({ lead_stage: val }),
      },
      {
        value: 'name',
        id: 'sl_no',
        label: 'Course',
        visible: false,
        data: courseByList,
        loading: courseByListLoading,
        onChange: (val) => setFilterHelper({ pref_course: val }),
      },
      {
        value: 'name',
        id: 'sl_no',
        label: 'Preferred Project',
        visible: true,
        data: preferredProjectByList,
        loading: preferredProjectByListLoading,
        onChange: (val) => setFilterHelper({ pref_project: val }),
      },
    ],
    [
      addedByList,
      sourceByList,
      subSourceByList,
      leadStatusByList,
      leadStageByList,
      courseByList,
      preferredProjectByList,
      leadTypesByList,
      ownerList,
      CSD,
      addedByListLoading,
      sourceByListLoading,
      subSourceByListLoading,
      leadStageByListLoading,
      leadStatusByListLoading,
      leadTypesByListLoading,
      courseByListLoading,
      preferredProjectByListLoading,
      ownerListLoading,
      setFilterHelper,
    ],
  );
  return (
    <Stack gap={1}>
      <Grid container spacing={1}>
        {AutoCompleteJson.map(({ data, label, id, value, visible, onChange, setKeyName = 'value' }, index) => (
          <Grid item xs={6} sm={4} md={2} lg={1.5} key={index}>
            <GlobalAutoCompleteMultiSelectNoFormik
              onChange={onChange}
              data={data}
              keys={{ id, value }}
              label={label}
              setKeyName={setKeyName}
              limitTags={1}
            />
          </Grid>
        ))}
      </Grid>
      <Button variant="outlined" sx={{ alignSelf: 'flex-end' }} onClick={handleSubmit}>
        Submit
      </Button>
    </Stack>
  );
}
